import { useCallback, useMemo, useState, type ChangeEvent, type MouseEvent } from "react";
import styles from "./SelectPropertyTypePage.module.css";
import { SignatureModal } from "../signatureModal/SignatureModal.tsx";
import { PropertyStepperScreen } from "@/shared/components/common/PropertyStepperScreen";
import { AddSupportingDocumentsModalPage } from "../addSupportingDocuments/AddSupportingDocumentsModalPage.tsx";
import { useSelectPropertyTypePageViewModal } from "@/features/protest/viewModal/selectPropertyTypeViewmodal";
import type { PropertyRelationshipSubmission } from "@/features/protest/viewModal/signatureViewmodal";
import { Modal } from "@/shared/modal";
import { ProtestStepperContent } from "@/features/protest/view/selectPropertyType/supportingDocumentsStepper/index.ts";
import { Images } from "@/assets/images1";

const HomeIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M3 10.5 12 3l9 7.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M5.5 10.5V21h13V10.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M10 21v-7h4v7" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const BuildingIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M4 21V7a2 2 0 0 1 2-2h7v16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M13 9h5a2 2 0 0 1 2 2v10H13V9Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M7 9h3M7 12h3M7 15h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 13h1.5M16 16h1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const FamilyIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM16 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M3.5 19.5c.5-3.1 2.4-5 4.5-5s4 1.9 4.5 5M11.5 19.5c.5-3.1 2.4-5 4.5-5s4 1.9 4.5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// const FileIcon = () => (
//   <svg
//     width="22"
//     height="22"
//     viewBox="0 0 24 24"
//     fill="none"
//     xmlns="http://www.w3.org/2000/svg"
//     aria-hidden="true"
//     focusable="false"
//   >
//     <path
//       d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-6Z"
//       stroke="currentColor"
//       strokeWidth="2"
//       strokeLinejoin="round"
//     />
//     <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
//   </svg>
// );

const ChevronRightIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M9.2 16.2 5.6 12.6l-1.4 1.4 5 5L20.2 8l-1.4-1.4-9.6 9.6Z" fill="currentColor" />
  </svg>
);

const UncheckedIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
  </svg>
);

type RelationshipType = "owner" | "family";
type OwnerRelationship = "Spouse" | "Child" | "Parent" | "Other";

type FamilyFieldErrors = {
  ownerRelationship?: string;
  filingReason?: string;
  drivingLicense?: string;
};

const ownerRelationshipOptions: OwnerRelationship[] = ["Spouse", "Child", "Parent", "Other"];

const REQUIRED_FIELD_ERROR = "This field is required";

export const SelectPropertyTypePage = () => {
  const vm = useSelectPropertyTypePageViewModal();
  const [relationshipType, setRelationshipType] = useState<RelationshipType>("owner");
  const [ownerRelationship, setOwnerRelationship] = useState<OwnerRelationship | null>(null);
  const [filingReason, setFilingReason] = useState("");
  const [drivingLicenseFile, setDrivingLicenseFile] = useState<File | null>(null);
  const [drivingLicenseFileName, setDrivingLicenseFileName] = useState("");
  const [familyFieldErrors, setFamilyFieldErrors] = useState<FamilyFieldErrors>({});

  const handleOwnerSelect = useCallback(() => {
    setRelationshipType("owner");
    setFamilyFieldErrors({});
  }, []);

  const handleFamilySelect = useCallback(() => {
    setRelationshipType("family");
  }, []);

  const handleOwnerRelationshipSelect = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    const value = event.currentTarget.dataset.relationship as OwnerRelationship | undefined;
    if (!value) return;
    setOwnerRelationship(value);
    setFamilyFieldErrors((prev) => ({ ...prev, ownerRelationship: undefined }));
  }, []);

  const handleReasonChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setFilingReason(event.target.value);
    setFamilyFieldErrors((prev) => ({ ...prev, filingReason: undefined }));
  }, []);

  const handleDrivingLicenseChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setDrivingLicenseFile(file);
    setDrivingLicenseFileName(file?.name || "");
    setFamilyFieldErrors((prev) => ({ ...prev, drivingLicense: undefined }));
  }, []);

  const validateFamilyFields = useCallback((): boolean => {
    if (relationshipType !== "family") return true;

    const errors: FamilyFieldErrors = {};

    if (!ownerRelationship) {
      errors.ownerRelationship = REQUIRED_FIELD_ERROR;
    }
    if (!filingReason.trim()) {
      errors.filingReason = REQUIRED_FIELD_ERROR;
    }
    if (!drivingLicenseFile) {
      errors.drivingLicense = REQUIRED_FIELD_ERROR;
    }

    setFamilyFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [relationshipType, ownerRelationship, filingReason, drivingLicenseFile]);

  const handleContinue = useCallback(() => {
    if (!validateFamilyFields()) return;
    vm.onSignature();
  }, [validateFamilyFields, vm]);

  const propertyRelationship = useMemo<PropertyRelationshipSubmission>(() => {
    if (relationshipType !== "family") {
      return { type: "owner" };
    }

    return {
      type: "family",
      relationshipToOwner: ownerRelationship ?? "",
      reasonForFiling: filingReason,
      drivingLicenseFile,
    };
  }, [relationshipType, ownerRelationship, filingReason, drivingLicenseFile]);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.title}>
          Select Your <span className={styles.titleAccent}>Property Type</span>
        </h2>

        <div className={styles.heroImageWrap}>
          <img src={Images.andyHoldingSign} alt="Andy holding sign" className={styles.heroImage} />
        </div>

        <section className={styles.relationshipCard} aria-labelledby="relationship-title">
          <h3 className={styles.relationshipTitle} id="relationship-title">
            My relationship to this property <span aria-hidden="true">*</span>
          </h3>

          <div
            className={styles.relationshipOptions}
            role="group"
            aria-label="My relationship to this property"
          >
            <button
              type="button"
              className={`${styles.relationshipOption} ${
                relationshipType === "owner" ? styles.relationshipOptionActive : ""
              }`}
              aria-pressed={relationshipType === "owner"}
              onClick={handleOwnerSelect}
            >
              <span className={styles.relationshipIcon} aria-hidden="true">
                <HomeIcon />
              </span>
              <span>Owner</span>
              {relationshipType === "owner" && (
                <span className={styles.relationshipCheck} aria-hidden="true">
                  <CheckIcon />
                </span>
              )}
            </button>

            <button
              type="button"
              className={`${styles.relationshipOption} ${
                relationshipType === "family" ? styles.relationshipOptionActive : ""
              }`}
              aria-pressed={relationshipType === "family"}
              onClick={handleFamilySelect}
            >
              <span className={styles.relationshipIcon} aria-hidden="true">
                <FamilyIcon />
              </span>
              <span>Family Member</span>
              {relationshipType === "family" && (
                <span className={styles.relationshipCheck} aria-hidden="true">
                  <CheckIcon />
                </span>
              )}
            </button>
          </div>

          {relationshipType === "family" && (
            <div className={styles.familyFields}>
              <div className={styles.familySectionLabel}>Filing on behalf of owner</div>

              <div className={styles.formGroup}>
                <div className={styles.fieldLabel}>
                  Relationship to owner <span aria-hidden="true">*</span>
                </div>
                <div
                  className={`${styles.relationshipChips} ${
                    familyFieldErrors.ownerRelationship ? styles.relationshipChipsError : ""
                  }`}
                  role="group"
                  aria-label="Relationship to owner"
                  aria-invalid={Boolean(familyFieldErrors.ownerRelationship)}
                  aria-describedby={
                    familyFieldErrors.ownerRelationship ? "owner-relationship-error" : undefined
                  }
                >
                  {ownerRelationshipOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      data-relationship={option}
                      className={`${styles.relationshipChip} ${
                        ownerRelationship === option ? styles.relationshipChipActive : ""
                      }`}
                      aria-pressed={ownerRelationship === option}
                      onClick={handleOwnerRelationshipSelect}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {familyFieldErrors.ownerRelationship && (
                  <div id="owner-relationship-error" className={styles.errorMessage} role="alert">
                    {familyFieldErrors.ownerRelationship}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.fieldLabel} htmlFor="filing-reason">
                  Reason for filing on their behalf <span aria-hidden="true">*</span>
                </label>
                <textarea
                  id="filing-reason"
                  className={`${styles.reasonTextarea} ${
                    familyFieldErrors.filingReason ? styles.inputError : ""
                  }`}
                  value={filingReason}
                  onChange={handleReasonChange}
                  placeholder="e.g. Owner is elderly and has authorised me to manage tax filings..."
                  rows={4}
                  aria-invalid={Boolean(familyFieldErrors.filingReason)}
                  aria-describedby={
                    familyFieldErrors.filingReason ? "filing-reason-error" : undefined
                  }
                />
                {familyFieldErrors.filingReason && (
                  <div id="filing-reason-error" className={styles.errorMessage} role="alert">
                    {familyFieldErrors.filingReason}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <div className={styles.fieldLabel}>
                  Upload Driving License <span aria-hidden="true">*</span>
                </div>
                <label
                  htmlFor="family-driving-license-upload"
                  className={`${styles.uploadLicenseBtn} ${
                    familyFieldErrors.drivingLicense ? styles.uploadLicenseBtnError : ""
                  }`}
                >
                  Upload Driving License
                </label>
                <input
                  id="family-driving-license-upload"
                  type="file"
                  className={styles.hiddenFileInput}
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp,image/*"
                  onChange={handleDrivingLicenseChange}
                  aria-invalid={Boolean(familyFieldErrors.drivingLicense)}
                  aria-describedby={
                    familyFieldErrors.drivingLicense ? "driving-license-error" : undefined
                  }
                />
                {drivingLicenseFileName && (
                  <div className={styles.selectedLicenseFileName}>{drivingLicenseFileName}</div>
                )}
                {familyFieldErrors.drivingLicense && (
                  <div id="driving-license-error" className={styles.errorMessage} role="alert">
                    {familyFieldErrors.drivingLicense}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        <section className={styles.typeSection} aria-labelledby="property-type-heading">
          <h3 className={styles.typeSectionTitle} id="property-type-heading">
            Select Property Type <span aria-hidden="true">*</span>
          </h3>
          <div className={styles.typeCardsShell}>
            <div className={styles.typeRow} role="group" aria-label="Select property type">
              <button
                type="button"
                className={`${styles.typeCard} ${vm.isSelected("RESIDENTIAL") ? styles.typeCardActive : ""}`}
                aria-pressed={vm.isSelected("RESIDENTIAL")}
                onClick={() => vm.onSelectPropertyType("RESIDENTIAL")}
              >
                <span className={styles.typeIcon} aria-hidden="true">
                  <HomeIcon />
                </span>
                <span className={styles.typeTextCol}>
                  <span className={styles.typeLabel}>Residential Property</span>
                  <span className={styles.typeDescription}>
                    Single-family home or residential property
                  </span>
                </span>
                <span className={styles.typeChevron} aria-hidden="true">
                  <ChevronRightIcon />
                </span>
              </button>

              <button
                type="button"
                className={`${styles.typeCard} ${vm.isSelected("COMMERCIAL") ? styles.typeCardActive : ""}`}
                aria-pressed={vm.isSelected("COMMERCIAL")}
                onClick={() => vm.onSelectPropertyType("COMMERCIAL")}
              >
                <span className={styles.typeIcon} aria-hidden="true">
                  <BuildingIcon />
                </span>
                <span className={styles.typeTextCol}>
                  <span className={styles.typeLabel}>Commercial Property</span>
                  <span className={styles.typeDescription}>
                    Commercial, Multifamily, Hotel or any commercial property
                  </span>
                </span>
                <span className={styles.typeChevron} aria-hidden="true">
                  <ChevronRightIcon />
                </span>
              </button>
            </div>
          </div>
        </section>

        {vm.hasSignedDocs ? (
          <div className={styles.signedShell}>
            <div className={styles.signedCard}>
              <div className={styles.signedHeader}>
                <div className={styles.signedHeaderLeft}>
                  <div className={styles.signedTitle}>Selected Property</div>
                  <div className={styles.signedAddress}>
                    {vm.propertyDetails?.address?.full_address ?? "—"}
                  </div>
                </div>
              </div>

              <div className={styles.signedTypeRow}>
                <span className={styles.signedTypeIcon} aria-hidden="true">
                  <HomeIcon />
                </span>
                <span className={styles.signedTypeText}>
                  {(
                    vm.propertyDetails?.property_type ||
                    vm.propertyDetails?.property_raw_type ||
                    vm.selectedTypeDisplay ||
                    ""
                  )
                    .toString()
                    .toLowerCase()
                    .includes("commercial")
                    ? "Commercial Property"
                    : "Residential Property"}
                </span>
              </div>

              <div className={styles.signedDivider} />

              <div className={styles.signedDocsTitle}>Documents Preview</div>
              <div className={styles.signedDocsList}>
                {vm.signedDocs.map((doc) => (
                  <button
                    key={doc.id}
                    type="button"
                    className={styles.signedDocRow}
                    disabled={!doc.iframeSrc}
                    aria-disabled={!doc.iframeSrc}
                    onClick={() => vm.onOpenDoc(doc)}
                  >
                    <span className={styles.signedDocCheck} aria-hidden="true">
                      {doc.isComplete ? <CheckIcon /> : <UncheckedIcon />}
                    </span>
                    <span className={styles.signedDocLabel}>{doc.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.docsShell}>
            {/* <div className={styles.docsCard}>
              <div className={styles.docsHeader}>
                <div className={styles.docsTitleWrap}>
                  <div className={styles.docsTitle}>Documents To Sign</div>
                  <div className={styles.docsSubtitle}>
                    Your Signature Will Be Used To Complete The Following Required Documents
                  </div>
                </div>

                {!vm.canView ? (
                  <div className={styles.docsHintPill}>
                    <span className={styles.docsHintDot} aria-hidden="true" />
                    <span>Please Select Your Property Type To View</span>
                  </div>
                ) : (
                  <div className={styles.docsHintSelected}>
                    Viewing: <span className={styles.docsHintSelectedStrong}>{vm.selected}</span>
                  </div>
                )}
              </div>

              <div className={styles.docsList}>
                {vm.docs.map((doc) => (
                  <div key={doc.id} className={styles.docRow}>
                    <div className={styles.docLeft}>
                      <span className={styles.docIcon} aria-hidden="true">
                        <FileIcon />
                      </span>
                      <span className={styles.docTitle}>{doc.title}</span>
                    </div>

                    <button
                      type="button"
                      className={styles.viewBtn}
                      disabled={!vm.canView}
                      aria-disabled={!vm.canView}
                      onClick={() => vm.onOpenDoc(doc)}
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            </div> */}
          </div>
        )}

        <div className={styles.continueRow}>
          <button
            type="button"
            className={styles.continueBtn}
            disabled={!vm.canView}
            aria-disabled={!vm.canView}
            onClick={handleContinue}
          >
            Continue
          </button>
        </div>
      </div>

      {vm.activeDoc && vm.selected && (
        <Modal
          isOpen
          onClose={vm.onCloseDoc}
          unconstrained
          className={styles.docModalContent}
          headerClassName={styles.docModalHeader}
          title={
            <>
              <div className={styles.docModalTitle}>{vm.activeDoc.title}</div>
              <div className={styles.docModalSubtitle}>{vm.selectedTypeDisplay}</div>
            </>
          }
          titleClassName={styles.docModalTitleGroup}
          bodyClassName={styles.docModalBody}
          ariaLabel="Document preview"
        >
          <iframe
            title={`${vm.activeDoc.title} PDF`}
            src={vm.iframeSrc}
            className={styles.docFrame}
            loading="lazy"
          />
        </Modal>
      )}

      <SignatureModal
        isOpen={vm.isSignatureModalOpen}
        selectedPropertyType={vm.selected}
        propertyRelationship={propertyRelationship}
        onClose={vm.onCloseSignatureModal}
        onSubmit={vm.onSignatureModalSubmit}
      />

      {vm.selected && (
        <Modal
          isOpen={vm.isWearAndTearStepperOpen}
          onClose={vm.onCloseStepper}
          showCloseButton={false}
          closeOnBackdropClick={false}
          closeOnEscape={false}
          unconstrained
          className={styles.stepperContent}
          bodyClassName={styles.stepperModalBody}
          ariaLabel="Property condition stepper"
        >
          <PropertyStepperScreen
            propertyType={vm.selected}
            propertyId={vm.selectedPropertyId}
            userId={vm.userId}
            currentStep={vm.wearAndTearCurrentStep}
            title={vm.currentStepTitle}
            onBack={vm.onStepperBack}
            onNext={vm.onStepperNext}
            onSkip={vm.onStepperSkip}
            onSubmit={vm.onStepperSubmit}
            residentialSteps={vm.residentialSteps}
            commercialSteps={vm.commercialSteps}
            lastStepHasAnySupportingDocuments={vm.lastStepHasAnySupportingDocuments}
          >
            <ProtestStepperContent
              isResidential={vm.isResidential}
              currentStep={vm.wearAndTearCurrentStep}
              residentialSteps={vm.residentialSteps}
              commercialSteps={vm.commercialSteps}
              localSupportingDocuments={vm.localSupportingDocuments}
            />
          </PropertyStepperScreen>
        </Modal>
      )}

      <AddSupportingDocumentsModalPage
        isOpen={vm.isSupportingDocsModalOpen}
        propertyType={vm.selected}
        selectedPropertyId={vm.selectedPropertyId}
        onCancel={vm.onCloseSupportingDocsModal}
        onConfirm={vm.onCloseSupportingDocsModal}
      />
    </section>
  );
};
